import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronLeft,
  Check,
  Copy,
  FlaskConical,
  Maximize2,
  Minimize2,
  RotateCcw,
  Search,
  Settings2,
  Shield,
  SlidersHorizontal,
  Terminal,
  X,
} from 'lucide-react';
import {
  DEV_STATE_PRESETS,
  ENTITLEMENT_OVERRIDE_OPTIONS,
  ONBOARDING_STEP_OPTIONS,
  PLAN_OPTIONS,
  ROLE_OPTIONS,
  getAuthPatchForRole,
  getProcessorPatch,
} from './presets';
import { hasDevStateOverrides, isDevStateLabEnabled, mergeDevState } from './devStateStore';
import { summarizeMerchantState } from './applyDevStateOverrides';

const TABS = [
  { value: 'presets', label: 'Presets' },
  { value: 'manual', label: 'Controls' },
  { value: 'inspector', label: 'Inspector' },
];

const SUMMARY_FIELDS = [
  { key: 'plan', label: 'Plan' },
  { key: 'entitlementOverride', label: 'Entitlement override' },
  { key: 'onboardingStep', label: 'Onboarding step' },
  { key: 'selectedPlanIntent', label: 'Selected plan intent' },
  { key: 'processor', label: 'Processor' },
  { key: 'stripeAccount', label: 'Stripe account' },
  { key: 'chargesEnabled', label: 'Charges enabled' },
];

const VIEWPORT_MARGIN = 12;
const MIN_WINDOW_WIDTH = 360;
const MIN_WINDOW_HEIGHT = 460;
const DEFAULT_WINDOW_WIDTH = 1180;
const DEFAULT_WINDOW_HEIGHT = 660;
const LAUNCHER_WIDTH = 216;
const LAUNCHER_MODIFIED_WIDTH = 284;
const LAUNCHER_HEIGHT = 32;
const LAUNCHER_COLLAPSED_SIZE = 36;
const LAUNCHER_DRAG_THRESHOLD = 5;
const LAUNCHER_SHELL_TRANSITION = { type: 'spring', stiffness: 520, damping: 38, mass: 0.72 };
const LAUNCHER_CONTENT_TRANSITION = { duration: 0.16, ease: 'easeOut' };
const LAUNCHER_DRAG_TRANSITION = { duration: 0 };
const COMPACT_WORKBENCH_WIDTH = 1040;
const WIDE_INSPECTOR_CONTENT_WIDTH = 1320;
const DEV_STATE_LAB_PORTAL_ROOT_ID = 'charitystack-dev-state-lab-root';

export function getDevStateLabPortalRoot(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc?.body) return null;

  const existingRoot = doc.getElementById(DEV_STATE_LAB_PORTAL_ROOT_ID);
  if (existingRoot) return existingRoot;

  const root = doc.createElement('div');
  root.id = DEV_STATE_LAB_PORTAL_ROOT_ID;
  root.dataset.devStateLabRoot = 'true';
  root.style.position = 'relative';
  root.style.zIndex = '2147483000';
  root.style.pointerEvents = 'auto';
  doc.body.appendChild(root);
  return root;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getViewportSize() {
  if (typeof window === 'undefined') {
    return { width: DEFAULT_WINDOW_WIDTH, height: DEFAULT_WINDOW_HEIGHT };
  }

  return { width: window.innerWidth, height: window.innerHeight };
}

function toPlainRect(rect) {
  if (!rect) return null;
  return {
    height: rect.height,
    left: rect.left,
    top: rect.top,
    width: rect.width,
  };
}

function clampWorkbenchFrame(frame) {
  const viewport = getViewportSize();
  const width = clamp(frame.width, MIN_WINDOW_WIDTH, Math.max(MIN_WINDOW_WIDTH, viewport.width - VIEWPORT_MARGIN * 2));
  const height = clamp(frame.height, MIN_WINDOW_HEIGHT, Math.max(MIN_WINDOW_HEIGHT, viewport.height - VIEWPORT_MARGIN * 2));

  return {
    width,
    height,
    left: clamp(frame.left, VIEWPORT_MARGIN, Math.max(VIEWPORT_MARGIN, viewport.width - width - VIEWPORT_MARGIN)),
    top: clamp(frame.top, VIEWPORT_MARGIN, Math.max(VIEWPORT_MARGIN, viewport.height - height - VIEWPORT_MARGIN)),
  };
}

function getDefaultWorkbenchFrame(triggerRect) {
  const viewport = getViewportSize();
  const width = Math.min(DEFAULT_WINDOW_WIDTH, viewport.width - VIEWPORT_MARGIN * 2);
  const height = Math.min(DEFAULT_WINDOW_HEIGHT, viewport.height - VIEWPORT_MARGIN * 4);
  const triggerLeft = triggerRect?.left ?? VIEWPORT_MARGIN;
  const triggerTop = triggerRect?.top ?? viewport.height - VIEWPORT_MARGIN;

  return clampWorkbenchFrame({
    width,
    height,
    left: triggerLeft,
    top: triggerTop - height - VIEWPORT_MARGIN,
  });
}

function getCollapsedWorkbenchFrame(triggerRect) {
  if (triggerRect) {
    return {
      height: Math.max(32, triggerRect.height || 32),
      left: triggerRect.left,
      top: triggerRect.top,
      width: Math.max(180, triggerRect.width || 180),
    };
  }

  const viewport = getViewportSize();
  return {
    height: 32,
    left: VIEWPORT_MARGIN,
    top: viewport.height - 44,
    width: 220,
  };
}

function clampLauncherFrame(frame) {
  const viewport = getViewportSize();
  const width = frame.width || LAUNCHER_WIDTH;
  const height = frame.height || LAUNCHER_HEIGHT;

  return {
    width,
    height,
    left: clamp(frame.left, VIEWPORT_MARGIN, Math.max(VIEWPORT_MARGIN, viewport.width - width - VIEWPORT_MARGIN)),
    top: clamp(frame.top, VIEWPORT_MARGIN, Math.max(VIEWPORT_MARGIN, viewport.height - height - VIEWPORT_MARGIN)),
  };
}

function getLauncherDimensions(collapsed, hasOverrides = false) {
  return {
    width: collapsed
      ? LAUNCHER_COLLAPSED_SIZE
      : hasOverrides
        ? LAUNCHER_MODIFIED_WIDTH
        : LAUNCHER_WIDTH,
    height: collapsed ? LAUNCHER_COLLAPSED_SIZE : LAUNCHER_HEIGHT,
  };
}

function getFallbackLauncherFrame(collapsed, hasOverrides = false) {
  const viewport = getViewportSize();
  const dimensions = getLauncherDimensions(collapsed, hasOverrides);

  return clampLauncherFrame({
    ...dimensions,
    left: VIEWPORT_MARGIN,
    top: viewport.height - dimensions.height - VIEWPORT_MARGIN,
  });
}

function clampLauncherFrameForState(frame, collapsed, hasOverrides = false) {
  return clampLauncherFrame({
    ...frame,
    ...getLauncherDimensions(collapsed, hasOverrides),
  });
}

function Field({ label, children }) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5 text-xs font-medium text-slate-400">
      <span>{label}</span>
      {children}
    </label>
  );
}

function Select({ value, onChange, children, ariaLabel }) {
  return (
    <select
      className="h-9 min-w-0 rounded-md border border-slate-700 bg-[#0f1218] px-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/25"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-label={ariaLabel}
    >
      {children}
    </select>
  );
}

function WorkbenchButton({ children, onClick, disabled, variant = 'default', ariaLabel }) {
  const classes =
    variant === 'primary'
      ? 'border-indigo-500/60 bg-indigo-500/15 text-indigo-200 hover:bg-indigo-500/25'
      : 'border-slate-700 bg-[#0f1218] text-slate-300 hover:border-slate-500 hover:bg-slate-800';

  return (
    <button
      type="button"
      className={`inline-flex h-8 items-center gap-2 rounded-md border px-2.5 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-indigo-400/30 disabled:cursor-not-allowed disabled:opacity-45 ${classes}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}

function SummaryRow({ label, raw, effective }) {
  const changed = String(raw) !== String(effective);
  return (
    <div className="grid grid-cols-[88px_minmax(0,1fr)_minmax(0,1fr)] items-center gap-2 border-b border-slate-800 px-3 py-2 text-xs last:border-b-0">
      <div className="font-medium text-slate-500">{label}</div>
      <div className="truncate font-mono text-slate-500">{raw}</div>
      <div className={changed ? 'truncate font-mono font-semibold text-indigo-200' : 'truncate font-mono text-slate-300'}>
        {effective}
      </div>
    </div>
  );
}

function StatusPill({ label, tone = 'slate' }) {
  const toneClasses = {
    green: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-300',
    amber: 'border-amber-500/25 bg-amber-500/10 text-amber-300',
    indigo: 'border-indigo-500/25 bg-indigo-500/10 text-indigo-300',
    slate: 'border-slate-700 bg-slate-800/70 text-slate-300',
  };

  return (
    <span className={`inline-flex h-6 items-center rounded-md border px-2 text-xs font-semibold ${toneClasses[tone]}`}>
      {label}
    </span>
  );
}

function groupPresets(presets) {
  return presets.reduce((groups, preset) => {
    const group = preset.group || 'Other';
    groups[group] = groups[group] || [];
    groups[group].push(preset);
    return groups;
  }, {});
}

function getPresetChips(preset) {
  const patch = preset?.state?.merchantPatch || {};
  const chips = [];

  if (patch.onboardingCurrentStep) chips.push(patch.onboardingCurrentStep);
  if (patch.onboardingSelectedPlan) chips.push(`intent ${patch.onboardingSelectedPlan}`);
  if (patch.billingDetails?.plan) chips.push(`plan ${patch.billingDetails.plan}`);
  if (patch.billingDetails?.entitlementOverrides) {
    chips.push(patch.billingDetails.entitlementOverrides);
  }
  if (patch.defaultPaymentProcessor) chips.push(patch.defaultPaymentProcessor);
  if (patch.stripeDetails?.chargesEnabled === true) chips.push('charges on');
  if (patch.stripeDetails?.chargesEnabled === false) chips.push('charges off');
  if (preset?.state?.auth?.effectiveRole) chips.push(preset.state.auth.effectiveRole);
  if (typeof preset?.state?.readOnly === 'boolean') {
    chips.push(preset.state.readOnly ? 'read only' : 'editable');
  }
  if (typeof preset?.state?.signup?.hasExistingOrg === 'boolean') {
    chips.push(preset.state.signup.hasExistingOrg ? 'has org' : 'no org');
  }

  return chips.slice(0, 3);
}

function InspectorPanel({ rawSummary, effectiveSummary, readOnly, rawReadOnly, state, compact = false }) {
  const hasOverrides = hasDevStateOverrides(state);

  return (
    <aside
      className={`min-h-0 border-t border-slate-800 bg-[#11151d] ${
        compact ? 'md:col-span-2' : 'md:border-l md:border-t-0'
      }`}
    >
      <div className="flex h-11 items-center justify-between border-b border-slate-800 px-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Inspector</div>
        <StatusPill label={hasOverrides ? 'modified' : 'no override'} tone={hasOverrides ? 'amber' : 'green'} />
      </div>
      <div className="min-h-0 overflow-auto">
        <div className="border-b border-slate-800 bg-[#0d1117] px-3 py-2">
          <div className="grid grid-cols-[88px_minmax(0,1fr)_minmax(0,1fr)] gap-2 text-xs font-semibold text-slate-500">
            <div>Field</div>
            <div>Raw</div>
            <div>Effective</div>
          </div>
        </div>
        <SummaryRow label="Plan" raw={rawSummary.plan} effective={effectiveSummary.plan} />
        <SummaryRow
          label="Override"
          raw={rawSummary.entitlementOverride}
          effective={effectiveSummary.entitlementOverride}
        />
        <SummaryRow
          label="Step"
          raw={rawSummary.onboardingStep}
          effective={effectiveSummary.onboardingStep}
        />
        <SummaryRow
          label="Intent"
          raw={rawSummary.selectedPlanIntent}
          effective={effectiveSummary.selectedPlanIntent}
        />
        <SummaryRow
          label="Processor"
          raw={rawSummary.processor}
          effective={effectiveSummary.processor}
        />
        <SummaryRow
          label="Charges"
          raw={rawSummary.chargesEnabled}
          effective={effectiveSummary.chargesEnabled}
        />
        <SummaryRow
          label="Read-only"
          raw={String(rawReadOnly)}
          effective={String(state.readOnly ?? readOnly)}
        />
        <div className="border-t border-slate-800 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Override JSON
          </div>
          <pre className="max-h-72 overflow-auto rounded-md border border-slate-800 bg-[#080b10] p-3 text-xs leading-5 text-slate-200">
            {JSON.stringify(state, null, 2)}
          </pre>
        </div>
      </div>
    </aside>
  );
}

function EffectiveStateSnapshot({ summary, compact = false }) {
  return (
    <section className="overflow-hidden rounded-md border border-slate-800 bg-[#11151d]">
      <div className="border-b border-slate-800 px-3 py-3">
        <h3 className="text-sm font-semibold text-slate-100">Current effective state</h3>
        <p className="mt-1 text-xs text-slate-500">
          What the dashboard is currently rendering after session overrides.
        </p>
      </div>
      <div className="divide-y divide-slate-800">
        {SUMMARY_FIELDS.map(({ key, label }) => (
          <div
            key={key}
            className={`grid items-start gap-1.5 px-3 py-2.5 ${
              compact ? 'grid-cols-1' : 'grid-cols-[148px_minmax(0,1fr)] gap-3'
            }`}
          >
            <div className="text-xs font-semibold text-slate-500">{label}</div>
            <div className="min-w-0 break-words font-mono text-sm leading-5 text-slate-100">
              {summary[key]}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function StorageSafetyPanel({ storageKey }) {
  return (
    <section className="overflow-hidden rounded-md border border-slate-800 bg-[#11151d]">
      <div className="border-b border-slate-800 px-3 py-3">
        <h3 className="text-sm font-semibold text-slate-100">Storage and safety</h3>
        <p className="mt-1 text-xs text-slate-500">
          Overrides live in this browser session for the active org.
        </p>
      </div>
      <div className="space-y-3 p-3">
        <div>
          <div className="mb-1.5 text-xs font-semibold text-slate-500">Session key</div>
          <code className="block rounded-md border border-slate-800 bg-[#080b10] px-3 py-2 font-mono text-xs leading-5 text-slate-300 [overflow-wrap:anywhere]">
            {storageKey}
          </code>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusPill label="frontend only" tone="indigo" />
          <StatusPill label="real token" tone="slate" />
          <StatusPill label="no database write" tone="green" />
        </div>
      </div>
    </section>
  );
}

export default function DevStateLab({
  rawMerchant,
  effectiveMerchant,
  state,
  setState,
  clearState,
  activeOrgId,
  storageKey,
  readOnly,
  rawReadOnly,
}) {
  const devStateLabEnabled = isDevStateLabEnabled();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('presets');
  const [activeGroup, setActiveGroup] = useState('Onboarding');
  const [groupQuery, setGroupQuery] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const [copied, setCopied] = useState(false);
  const triggerRef = useRef(null);
  const launcherDragStateRef = useRef(null);
  const suppressLauncherClickRef = useRef(false);
  const [triggerRect, setTriggerRect] = useState(null);
  const [frame, setFrame] = useState(null);
  const [launcherMode, setLauncherMode] = useState('inline');
  const [launcherCollapsed, setLauncherCollapsed] = useState(false);
  const [launcherFrame, setLauncherFrame] = useState(null);
  const [isLauncherDragging, setIsLauncherDragging] = useState(false);
  const inlineAnchorRef = useRef(null);
  const [inlineAnchorFrame, setInlineAnchorFrame] = useState(null);

  const rawSummary = useMemo(() => summarizeMerchantState(rawMerchant), [rawMerchant]);
  const effectiveSummary = useMemo(
    () => summarizeMerchantState(effectiveMerchant),
    [effectiveMerchant],
  );
  const groupedPresets = useMemo(() => groupPresets(DEV_STATE_PRESETS), []);
  const groupNames = useMemo(() => Object.keys(groupedPresets), [groupedPresets]);
  const displayedGroupNames = groupNames.filter((group) =>
    group.toLowerCase().includes(groupQuery.trim().toLowerCase()),
  );
  const visibleGroup = groupNames.includes(activeGroup) ? activeGroup : groupNames[0];
  const visiblePresets = groupedPresets[visibleGroup] || [];
  const hasOverrides = hasDevStateOverrides(state);
  const currentRole = state.auth?.effectiveRole || '';
  const effectiveFrame = frame || getDefaultWorkbenchFrame(triggerRect);
  const isCompactWorkbench = effectiveFrame.width < COMPACT_WORKBENCH_WIDTH;
  const hasWideInspectorContent = effectiveFrame.width >= WIDE_INSPECTOR_CONTENT_WIDTH;
  const workbenchGridClass = isCompactWorkbench
    ? 'grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)]'
    : 'grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[220px_minmax(340px,1fr)_340px]';
  const inspectorContentGridClass = hasWideInspectorContent
    ? 'grid gap-3 grid-cols-[minmax(0,1fr)_minmax(300px,0.85fr)]'
    : 'grid gap-3';
  const collapsedFrame = getCollapsedWorkbenchFrame(triggerRect);
  const launcherDimensions = getLauncherDimensions(launcherCollapsed, hasOverrides);
  const portalLauncherFrame =
    launcherMode === 'floating'
      ? clampLauncherFrameForState(launcherFrame || collapsedFrame, launcherCollapsed, hasOverrides)
      : clampLauncherFrameForState(
          inlineAnchorFrame || getFallbackLauncherFrame(launcherCollapsed, hasOverrides),
          launcherCollapsed,
          hasOverrides,
        );

  useEffect(() => {
    if (!hasOverrides) {
      setSelectedPresetId(null);
    }
  }, [hasOverrides]);

  useLayoutEffect(() => {
    if (!devStateLabEnabled || launcherMode !== 'inline' || typeof window === 'undefined') {
      return undefined;
    }

    const updateInlineAnchorFrame = () => {
      const nextFrame = toPlainRect(inlineAnchorRef.current?.getBoundingClientRect());
      if (!nextFrame) return;

      setInlineAnchorFrame((currentFrame) => {
        if (
          currentFrame &&
          currentFrame.height === nextFrame.height &&
          currentFrame.left === nextFrame.left &&
          currentFrame.top === nextFrame.top &&
          currentFrame.width === nextFrame.width
        ) {
          return currentFrame;
        }
        return nextFrame;
      });
    };

    updateInlineAnchorFrame();

    const observer =
      typeof ResizeObserver !== 'undefined' && inlineAnchorRef.current
        ? new ResizeObserver(updateInlineAnchorFrame)
        : null;
    observer?.observe(inlineAnchorRef.current);
    window.addEventListener('resize', updateInlineAnchorFrame);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateInlineAnchorFrame);
    };
  }, [
    devStateLabEnabled,
    hasOverrides,
    launcherCollapsed,
    launcherDimensions.height,
    launcherDimensions.width,
    launcherMode,
  ]);

  const applyPreset = (preset) => {
    setState((current) => mergeDevState(current, preset.state));
    setSelectedPresetId(preset.id);
    setTab('inspector');
  };

  const patchState = (patch) => {
    setState((current) => mergeDevState(current, patch));
    setSelectedPresetId(null);
  };

  const handleClearState = () => {
    clearState();
    setSelectedPresetId(null);
  };

  const copyDebugState = async () => {
    const payload = {
      activeOrgId,
      storageKey,
      devState: state,
      raw: rawSummary,
      effective: effectiveSummary,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (error) {
      console.warn('[DevStateLab] failed to copy debug state', error);
    }
  };

  const openWorkbench = () => {
    const nextTriggerRect = toPlainRect(triggerRef.current?.getBoundingClientRect());
    setTriggerRect(nextTriggerRect);
    setFrame((currentFrame) =>
      clampWorkbenchFrame(currentFrame || getDefaultWorkbenchFrame(nextTriggerRect)),
    );
    setOpen(true);
  };

  const closeWorkbench = () => {
    setTriggerRect(toPlainRect(triggerRef.current?.getBoundingClientRect()) || triggerRect);
    setOpen(false);
  };

  const handleLauncherClick = () => {
    if (suppressLauncherClickRef.current) {
      suppressLauncherClickRef.current = false;
      return;
    }

    if (open) {
      closeWorkbench();
    } else {
      openWorkbench();
    }
  };

  const handleLauncherPointerDown = (event) => {
    if (event.button !== 0) return;

    event.preventDefault();
    const startRect = toPlainRect(event.currentTarget.getBoundingClientRect());
    if (!startRect) return;
    const startFrame = portalLauncherFrame || startRect;

    launcherDragStateRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      frame: clampLauncherFrameForState(startFrame, launcherCollapsed, hasOverrides),
      windowFrame: open ? clampWorkbenchFrame(effectiveFrame) : null,
      latestFrame: null,
      latestWindowFrame: null,
      hasMoved: false,
    };
    suppressLauncherClickRef.current = false;

    const handlePointerMove = (moveEvent) => {
      const dragState = launcherDragStateRef.current;
      if (!dragState) return;
      moveEvent.preventDefault();

      const deltaX = moveEvent.clientX - dragState.pointerX;
      const deltaY = moveEvent.clientY - dragState.pointerY;
      const distance = Math.hypot(deltaX, deltaY);

      if (!dragState.hasMoved && distance < LAUNCHER_DRAG_THRESHOLD) {
        return;
      }

      dragState.hasMoved = true;
      suppressLauncherClickRef.current = true;
      setIsLauncherDragging(true);
      const nextFrame = clampLauncherFrameForState({
        ...dragState.frame,
        left: dragState.frame.left + deltaX,
        top: dragState.frame.top + deltaY,
      }, launcherCollapsed, hasOverrides);
      const nextWindowFrame = dragState.windowFrame
        ? clampWorkbenchFrame({
            ...dragState.windowFrame,
            left: dragState.windowFrame.left + deltaX,
            top: dragState.windowFrame.top + deltaY,
          })
        : null;
      dragState.latestFrame = nextFrame;
      dragState.latestWindowFrame = nextWindowFrame;
      setLauncherMode('floating');
      setLauncherFrame(nextFrame);
      if (nextWindowFrame) {
        setFrame(nextWindowFrame);
      }
    };

    const handlePointerRelease = () => {
      const dragState = launcherDragStateRef.current;
      launcherDragStateRef.current = null;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerRelease);
      window.removeEventListener('pointercancel', handlePointerRelease);
      setIsLauncherDragging(false);

      if (!dragState?.hasMoved || !dragState.latestFrame) return;

      const latestFrame = dragState.latestFrame;
      setLauncherMode('floating');
      setLauncherFrame(clampLauncherFrameForState(latestFrame, launcherCollapsed, hasOverrides));
      if (dragState.latestWindowFrame) {
        setFrame(clampWorkbenchFrame(dragState.latestWindowFrame));
      }
      window.setTimeout(() => {
        suppressLauncherClickRef.current = false;
      }, 250);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerRelease);
    window.addEventListener('pointercancel', handlePointerRelease);
  };

  useEffect(() => {
    const handleResize = () => {
      if (open) {
        setFrame((currentFrame) =>
          currentFrame ? clampWorkbenchFrame(currentFrame) : getDefaultWorkbenchFrame(triggerRect),
        );
      }
      setLauncherFrame((currentFrame) => {
        if (!currentFrame || launcherMode === 'inline') return currentFrame;
        return clampLauncherFrameForState(currentFrame, launcherCollapsed, hasOverrides);
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [hasOverrides, launcherCollapsed, launcherMode, open, triggerRect]);

  const renderManualControls = () => (
    <div className="grid gap-4 xl:grid-cols-2">
      <section className="rounded-md border border-slate-800 bg-[#11151d]">
        <div className="flex h-10 items-center gap-2 border-b border-slate-800 px-3 text-sm font-semibold text-slate-100">
          <SlidersHorizontal className="h-4 w-4 text-indigo-300" aria-hidden="true" />
          Org State
        </div>
        <div className="grid gap-3 p-3 sm:grid-cols-2">
          <Field label="Plan">
            <Select
              value={state.merchantPatch?.billingDetails?.plan || ''}
              onChange={(value) =>
                patchState({ merchantPatch: { billingDetails: { plan: value } } })
              }
              ariaLabel="Plan"
            >
              <option value="" disabled>
                Select
              </option>
              {PLAN_OPTIONS.map((plan) => (
                <option key={plan} value={plan}>
                  {plan}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Override">
            <Select
              value={state.merchantPatch?.billingDetails?.entitlementOverrides || ''}
              onChange={(value) =>
                patchState({
                  merchantPatch: {
                    billingDetails: {
                      entitlementOverrides: value === 'none' ? null : value,
                    },
                  },
                })
              }
              ariaLabel="Entitlement override"
            >
              <option value="" disabled>
                Select
              </option>
              <option value="none">None</option>
              {ENTITLEMENT_OVERRIDE_OPTIONS.map((override) => (
                <option key={override} value={override}>
                  {override}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Covered fees">
            <Select
              value={
                typeof state.merchantPatch?.billingDetails?.coveredCommitment === 'boolean'
                  ? String(state.merchantPatch.billingDetails.coveredCommitment)
                  : ''
              }
              onChange={(value) =>
                patchState({
                  merchantPatch: {
                    billingDetails: { coveredCommitment: value === 'true' },
                  },
                })
              }
              ariaLabel="Covered fees"
            >
              <option value="" disabled>
                Select
              </option>
              <option value="true">On</option>
              <option value="false">Off</option>
            </Select>
          </Field>
          <Field label="Processor">
            <Select
              value=""
              onChange={(value) => patchState({ merchantPatch: getProcessorPatch(value) })}
              ariaLabel="Processor"
            >
              <option value="" disabled>
                Select
              </option>
              <option value="UNSET">Unset</option>
              <option value="STRIPE_PENDING">Stripe pending</option>
              <option value="STRIPE_ENABLED">Stripe enabled</option>
              <option value="FINIX">Finix</option>
            </Select>
          </Field>
        </div>
      </section>

      <section className="rounded-md border border-slate-800 bg-[#11151d]">
        <div className="flex h-10 items-center gap-2 border-b border-slate-800 px-3 text-sm font-semibold text-slate-100">
          <Settings2 className="h-4 w-4 text-indigo-300" aria-hidden="true" />
          Onboarding
        </div>
        <div className="grid gap-3 p-3 sm:grid-cols-2">
          <Field label="Step">
            <Select
              value={state.merchantPatch?.onboardingCurrentStep || ''}
              onChange={(value) =>
                patchState({ merchantPatch: { onboardingCurrentStep: value } })
              }
              ariaLabel="Onboarding step"
            >
              <option value="" disabled>
                Select
              </option>
              {ONBOARDING_STEP_OPTIONS.map((step) => (
                <option key={step} value={step}>
                  {step}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Website">
            <Select
              value={
                typeof state.merchantPatch?.hasWebsite === 'boolean'
                  ? String(state.merchantPatch.hasWebsite)
                  : ''
              }
              onChange={(value) =>
                patchState({ merchantPatch: { hasWebsite: value === 'true' } })
              }
              ariaLabel="Website path"
            >
              <option value="" disabled>
                Select
              </option>
              <option value="true">Has website</option>
              <option value="false">No website</option>
            </Select>
          </Field>
          <Field label="Plan intent">
            <Select
              value={state.merchantPatch?.onboardingSelectedPlan || ''}
              onChange={(value) =>
                patchState({
                  merchantPatch: {
                    onboardingSelectedPlan: value === 'none' ? null : value,
                  },
                })
              }
              ariaLabel="Selected plan intent"
            >
              <option value="" disabled>
                Select
              </option>
              <option value="none">None</option>
              <option value="BASE">BASE</option>
              <option value="PLUS">PLUS</option>
            </Select>
          </Field>
          <Field label="Activation done">
            <Select
              value={
                typeof state.merchantPatch?.driverFlowsCompleted?.activation === 'boolean'
                  ? String(state.merchantPatch.driverFlowsCompleted.activation)
                  : ''
              }
              onChange={(value) =>
                patchState({
                  merchantPatch: {
                    driverFlowsCompleted: { activation: value === 'true' },
                  },
                })
              }
              ariaLabel="Activation flow completed"
            >
              <option value="" disabled>
                Select
              </option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Select>
          </Field>
        </div>
      </section>

      <section className="rounded-md border border-slate-800 bg-[#11151d] xl:col-span-2">
        <div className="flex h-10 items-center gap-2 border-b border-slate-800 px-3 text-sm font-semibold text-slate-100">
          <Shield className="h-4 w-4 text-indigo-300" aria-hidden="true" />
          User State
        </div>
        <div className="grid gap-3 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Role">
            <Select
              value={currentRole}
              onChange={(value) => patchState({ auth: getAuthPatchForRole(value) })}
              ariaLabel="Role"
            >
              <option value="" disabled>
                Select
              </option>
              {ROLE_OPTIONS.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Read-only">
            <Select
              value={state.readOnly == null ? 'inherit' : String(state.readOnly)}
              onChange={(value) =>
                patchState({ readOnly: value === 'inherit' ? null : value === 'true' })
              }
              ariaLabel="Read-only mode"
            >
              <option value="inherit">Raw user</option>
              <option value="false">Off</option>
              <option value="true">On</option>
            </Select>
          </Field>
          <Field label="Has org">
            <Select
              value={
                typeof state.signup?.hasExistingOrg === 'boolean'
                  ? String(state.signup.hasExistingOrg)
                  : 'inherit'
              }
              onChange={(value) =>
                patchState({
                  signup: {
                    hasExistingOrg: value === 'inherit' ? null : value === 'true',
                  },
                })
              }
              ariaLabel="Has existing organization"
            >
              <option value="inherit">Raw user</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Select>
          </Field>
          <Field label="Signup complete">
            <Select
              value={
                typeof state.signup?.signupComplete === 'boolean'
                  ? String(state.signup.signupComplete)
                  : 'inherit'
              }
              onChange={(value) =>
                patchState({
                  signup: {
                    signupComplete: value === 'inherit' ? null : value === 'true',
                  },
                })
              }
              ariaLabel="Signup complete"
            >
              <option value="inherit">Raw user</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Select>
          </Field>
        </div>
      </section>
    </div>
  );

  const toggleLauncherCollapsed = () => {
    const nextCollapsed = !launcherCollapsed;
    const currentRect = toPlainRect(triggerRef.current?.getBoundingClientRect());
    const sourceFrame = launcherFrame || currentRect || collapsedFrame;
    setLauncherFrame(clampLauncherFrameForState(sourceFrame, nextCollapsed, hasOverrides));
    setLauncherCollapsed(nextCollapsed);
  };

  const handleCollapsedLauncherClick = () => {
    if (suppressLauncherClickRef.current) {
      suppressLauncherClickRef.current = false;
      return;
    }
    toggleLauncherCollapsed();
  };

  const triggerActionLabel = open ? 'Hide' : 'Open';
  const renderLauncherAnchor = () => (
    <span
      ref={inlineAnchorRef}
      aria-hidden="true"
      className="inline-flex max-w-full shrink-0"
      style={{
        height: launcherDimensions.height,
        width: launcherDimensions.width,
      }}
    />
  );

  const renderLauncherButton = ({ detached = false } = {}) => {
    return (
      <motion.div
        ref={triggerRef}
        className={
          detached
            ? 'relative z-10 flex h-full w-full cursor-grab touch-none select-none items-center justify-center overflow-hidden rounded-lg border border-slate-700 bg-[#11151d] text-left text-slate-100 shadow-2xl shadow-slate-950/35 transition-colors hover:bg-[#171b24] active:cursor-grabbing'
            : 'relative z-10 inline-flex max-w-full shrink-0 cursor-grab touch-none select-none items-center justify-center overflow-hidden rounded-lg border border-slate-700 bg-[#11151d] text-left text-slate-100 shadow-sm transition-colors hover:bg-[#171b24] active:cursor-grabbing'
        }
        title="Dev State Workbench"
        initial={false}
        animate={
          detached
            ? undefined
            : {
                width: launcherDimensions.width,
                height: launcherDimensions.height,
              }
        }
        transition={LAUNCHER_SHELL_TRANSITION}
        onPointerDown={handleLauncherPointerDown}
      >
        <AnimatePresence initial={false} mode="wait">
          {launcherCollapsed ? (
            <motion.button
              key="collapsed-launcher"
              type="button"
              className="relative inline-flex h-full w-full items-center justify-center text-indigo-200 focus:outline-none focus:ring-4 focus:ring-indigo-400/20"
              aria-label="Expand dev state workbench launcher"
              title="Expand Dev State Workbench"
              initial={{ opacity: 0, scale: 0.78, rotate: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.82, rotate: 8 }}
              transition={LAUNCHER_CONTENT_TRANSITION}
              onClick={handleCollapsedLauncherClick}
            >
              <Terminal className="h-4 w-4" aria-hidden="true" />
              {hasOverrides && (
                <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-amber-400" />
              )}
            </motion.button>
          ) : (
            <motion.div
              key="expanded-launcher"
              className="flex h-full w-full items-center justify-between gap-0.5 p-0.5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={LAUNCHER_CONTENT_TRANSITION}
            >
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-2 text-left focus:outline-none focus:ring-4 focus:ring-indigo-400/20"
                aria-label={open ? 'Hide dev state workbench' : 'Open dev state workbench'}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-controls="dev-state-workbench-window"
                onClick={handleLauncherClick}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Terminal className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
                  <span className="font-mono text-sm text-slate-300">Workbench</span>
                  {hasOverrides && <StatusPill label="modified" tone="amber" />}
                </span>
                <span className="hidden shrink-0 items-center gap-1.5 text-xs font-semibold text-indigo-200 sm:flex">
                  {triggerActionLabel}
                  {open ? (
                    <Minimize2 className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Maximize2 className="h-4 w-4" aria-hidden="true" />
                  )}
                </span>
              </button>
              <button
                type="button"
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-800 hover:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
                aria-label="Collapse dev state workbench launcher"
                title="Collapse to Workbench icon"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={toggleLauncherCollapsed}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  const detachedWorkbenchLayer = (
    <>
      <AnimatePresence>
        {portalLauncherFrame ? (
          <motion.div
            key="dev-state-workbench-launcher"
            className="pointer-events-auto fixed z-[2147483000]"
            initial={toPlainRect(triggerRef.current?.getBoundingClientRect()) || portalLauncherFrame}
            animate={portalLauncherFrame}
            exit={portalLauncherFrame}
            transition={
              isLauncherDragging
                ? LAUNCHER_DRAG_TRANSITION
                : { type: 'spring', stiffness: 440, damping: 38, mass: 0.8 }
            }
          >
            {renderLauncherButton({ detached: true })}
          </motion.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {open ? (
          <motion.section
            key="dev-state-workbench-window"
            id="dev-state-workbench-window"
            role="dialog"
            aria-modal="false"
            aria-labelledby="dev-state-workbench-title"
            aria-describedby="dev-state-workbench-description"
            className="fixed z-[2147482990] flex min-h-0 flex-col overflow-hidden rounded-lg border border-slate-700 bg-[#0d1117] p-0 text-slate-100 shadow-2xl shadow-slate-950/40"
            initial={{
              ...collapsedFrame,
              opacity: 0,
              scale: 0.92,
            }}
            animate={{
              ...effectiveFrame,
              opacity: 1,
              scale: 1,
            }}
            exit={{
              ...getCollapsedWorkbenchFrame(toPlainRect(triggerRef.current?.getBoundingClientRect()) || triggerRect),
              opacity: 0,
              scale: 0.92,
            }}
            transition={
              isLauncherDragging
                ? LAUNCHER_DRAG_TRANSITION
                : {
                    type: 'spring',
                    stiffness: 380,
                    damping: 38,
                    mass: 0.9,
                  }
            }
          >
            <div id="dev-state-workbench-description" className="sr-only">
              Simulate dashboard user and organization state in local and development environments.
            </div>
            <header
              className="flex h-14 shrink-0 items-center justify-between border-b border-slate-800 bg-[#11151d] px-3"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-indigo-500/30 bg-indigo-500/15">
                  <FlaskConical className="h-4 w-4 text-indigo-200" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h2
                    id="dev-state-workbench-title"
                    className="truncate text-sm font-semibold text-slate-100"
                  >
                    Dev State Workbench
                  </h2>
                  <div className="mt-0.5 flex min-w-0 items-center gap-2 text-xs text-slate-500">
                    <span className="truncate font-mono">
                      {activeOrgId || rawMerchant?.organizationId || 'no-active-org'}
                    </span>
                    <StatusPill label="dev only" tone="indigo" />
                    <StatusPill label={hasOverrides ? 'modified' : 'no override'} tone={hasOverrides ? 'amber' : 'green'} />
                  </div>
                </div>
              </div>
              <div
                className="flex items-center gap-2 pl-3"
                onPointerDown={(event) => event.stopPropagation()}
              >
                <WorkbenchButton onClick={copyDebugState}>
                  {copied ? (
                    <Check className="h-4 w-4 text-emerald-300" aria-hidden="true" />
                  ) : (
                    <Copy className="h-4 w-4" aria-hidden="true" />
                  )}
                  <span className="hidden sm:inline">{copied ? 'Copied' : 'Copy JSON'}</span>
                </WorkbenchButton>
                <WorkbenchButton onClick={handleClearState} disabled={!hasOverrides}>
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">Clear</span>
                </WorkbenchButton>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-800 hover:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
                  aria-label="Close"
                  onClick={closeWorkbench}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </header>

            <div className="flex h-11 shrink-0 items-center justify-between border-b border-slate-800 bg-[#0f131a] px-3">
              <div
                className="flex h-full items-center gap-1"
                role="tablist"
                aria-label="Dev state workbench tabs"
              >
                {TABS.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    role="tab"
                    aria-selected={tab === item.value}
                    className={`relative h-full px-3 text-sm font-semibold transition ${
                      tab === item.value
                        ? 'text-indigo-200'
                        : 'text-slate-500 hover:text-slate-200'
                    }`}
                    onClick={() => setTab(item.value)}
                  >
                    {item.label}
                    {tab === item.value && (
                      <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-indigo-400" />
                    )}
                  </button>
                ))}
              </div>
              <div className="hidden items-center gap-2 md:flex">
                <StatusPill label={`plan ${effectiveSummary.plan}`} />
                <StatusPill label={effectiveSummary.onboardingStep} />
                <StatusPill label={effectiveSummary.processor} />
              </div>
            </div>

            <div className={workbenchGridClass}>
              <aside className="min-h-0 border-b border-slate-800 bg-[#0b0f15] md:border-b-0 md:border-r">
                <div className="border-b border-slate-800 p-3">
                  <label className="flex h-9 items-center gap-2 rounded-md border border-slate-800 bg-[#080b10] px-3 text-sm text-slate-500 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-500/20">
                    <Search className="h-4 w-4" aria-hidden="true" />
                    <span className="sr-only">Filter state groups</span>
                    <input
                      className="min-w-0 flex-1 bg-transparent text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none"
                      value={groupQuery}
                      onChange={(event) => setGroupQuery(event.target.value)}
                      placeholder="Filter state groups"
                    />
                  </label>
                </div>
                <div className="max-h-48 overflow-auto p-2 md:max-h-none">
                  {displayedGroupNames.map((group) => (
                    <button
                      key={group}
                      type="button"
                      className={`mb-1 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-semibold transition ${
                        group === visibleGroup
                          ? 'bg-indigo-500/15 text-indigo-200'
                          : 'text-slate-400 hover:bg-slate-800/70 hover:text-slate-100'
                      }`}
                      onClick={() => {
                        setActiveGroup(group);
                        setTab('presets');
                      }}
                    >
                      <span>{group}</span>
                      <span className="rounded bg-slate-800 px-1.5 py-0.5 text-xs text-slate-400">
                        {groupedPresets[group].length}
                      </span>
                    </button>
                  ))}
                  {displayedGroupNames.length === 0 && (
                    <div className="px-3 py-5 text-sm text-slate-500">No matching groups</div>
                  )}
                </div>
              </aside>

              <main className="min-h-0 overflow-auto bg-[#0d1117] p-3">
                {tab === 'presets' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-100">{visibleGroup}</h3>
                        <p className="mt-1 text-xs text-slate-500">
                          {visiblePresets.length} scenarios
                        </p>
                      </div>
                      <StatusPill label="session scoped" tone="slate" />
                    </div>
                    <div className="overflow-hidden rounded-md border border-slate-800">
                      {visiblePresets.map((preset) => (
                        <button
                          key={preset.id}
                          type="button"
                          className={`grid w-full grid-cols-[88px_minmax(0,1fr)_72px] items-center gap-3 border-b border-slate-800 px-3 py-3 text-left transition last:border-b-0 ${
                            selectedPresetId === preset.id
                              ? 'bg-indigo-500/10 outline outline-1 outline-indigo-500/60'
                              : 'bg-[#11151d] hover:bg-[#171b24]'
                          }`}
                          onClick={() => applyPreset(preset)}
                          aria-pressed={selectedPresetId === preset.id}
                        >
                          <span className="font-mono text-xs font-semibold text-indigo-300">
                            {preset.group}
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold text-slate-100">
                              {preset.title}
                            </span>
                            <span className="mt-1 block truncate text-xs text-slate-500">
                              {preset.description}
                            </span>
                            <span className="mt-2 flex min-w-0 flex-wrap gap-1">
                              {getPresetChips(preset).map((chip) => (
                                <span
                                  key={chip}
                                  className="rounded border border-slate-700 bg-[#0d1117] px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-400"
                                >
                                  {chip}
                                </span>
                              ))}
                            </span>
                          </span>
                          <span
                            className={`justify-self-end rounded-md border px-2 py-1 text-xs font-semibold ${
                              selectedPresetId === preset.id
                                ? 'border-indigo-500/50 bg-indigo-500/15 text-indigo-200'
                                : 'border-slate-700 text-slate-400'
                            }`}
                          >
                            {selectedPresetId === preset.id ? 'applied' : 'apply'}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {tab === 'manual' && renderManualControls()}

                {tab === 'inspector' && (
                  <div className={inspectorContentGridClass}>
                    <EffectiveStateSnapshot
                      summary={effectiveSummary}
                      compact={isCompactWorkbench}
                    />
                    <StorageSafetyPanel storageKey={storageKey} />
                  </div>
                )}
              </main>

              <InspectorPanel
                rawSummary={rawSummary}
                effectiveSummary={effectiveSummary}
                readOnly={readOnly}
                rawReadOnly={rawReadOnly}
                state={state}
                compact={isCompactWorkbench}
              />
            </div>

            <footer className="flex h-10 shrink-0 items-center justify-between border-t border-slate-800 bg-[#11151d] px-3">
              <div className="flex min-w-0 items-center gap-2 text-sm text-slate-500">
                <Shield className="h-4 w-4 shrink-0 text-indigo-300" aria-hidden="true" />
                <span className="font-mono">Session override</span>
                <StatusPill label="frontend only" tone="indigo" />
                <StatusPill label="no database write" tone="green" />
                <span className="hidden truncate font-mono text-xs sm:inline">
                  scope {activeOrgId || rawMerchant?.organizationId || 'none'}
                </span>
              </div>
              <StatusPill label={hasOverrides ? 'modified' : 'no override'} tone={hasOverrides ? 'amber' : 'green'} />
            </footer>
          </motion.section>
        ) : null}
      </AnimatePresence>
    </>
  );

  return (
    <>
      {devStateLabEnabled && launcherMode === 'inline' ? renderLauncherAnchor() : null}
      {devStateLabEnabled
        ? typeof document !== 'undefined'
          ? createPortal(detachedWorkbenchLayer, getDevStateLabPortalRoot(document) || document.body)
          : detachedWorkbenchLayer
        : null}
    </>
  );
}
